import { MouseEventHandler, useContext, useMemo } from 'react';
import { AdCampaignsDataContext } from 'src/contexts/AdCampaignsDataContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { PosProgressBar } from 'src/core/POS/PosProgressBar/PosProgressBar';
import { vars } from 'src/core/themes';
import { ColorsV2 } from 'src/core/themes/constants/colors';
import { Button, Stack } from 'src/core/ui';
import { CampaignModal } from 'src/modals/SponsoredListings/CampaignModal';
import { BoostIcon } from 'src/svgs/BoostIcon';
import { BoostIconLight } from 'src/svgs/BoostIconLight';
import { ContentId } from 'src/utils/constants/contentId';
import { formatCurrency } from 'src/utils/numberFormatter';
import { AdCampaignState } from 'src/WebApiController';

export type BoostListingButtonProps = {
  eventId?: number | null;
  shape?: 'pill' | 'rect';
  height?: number | string;
  showIcon?: boolean;
};
export const BoostListingButton = ({
  eventId,
  height,
  showIcon,
}: BoostListingButtonProps) => {
  const { isDarkMode } = useSiteTheme();
  const { setModal } = useContext(ModalContext);
  const { allCampaignData, isEventsEnabledForSponsoredListingsLoading } =
    useContext(AdCampaignsDataContext);

  const onClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!eventId) {
      return;
    }

    setModal({
      children: <CampaignModal eventId={eventId} campaign={campaignForEvent} />,
      clickOutsideToClose: true,
      size: 'slide-in-md',
    });
  };

  const isEnabled = useMemo(
    () => isEventsEnabledForSponsoredListingsLoading(eventId ?? null),
    [eventId, isEventsEnabledForSponsoredListingsLoading]
  );

  if (!isEnabled) {
    return null;
  }

  const campaignForEvent = allCampaignData?.[eventId!];
  const BoostIconDisplay = isDarkMode ? BoostIconLight : BoostIcon;

  return (
    <Button
      variant="outlineBrand"
      shape="rect"
      style={{
        height,
        paddingLeft: vars.spacing.sm,
        paddingRight: vars.spacing.sm,
        borderColor:
          campaignForEvent &&
          campaignForEvent.campaignState !== AdCampaignState.Active
            ? ColorsV2.error020
            : undefined,
      }}
      color={vars.color.textBrandV2}
      onClick={onClick}
    >
      {campaignForEvent && (
        <Stack gap="m" alignItems="center" style={{ flex: 1 }}>
          <PosProgressBar
            style={{
              width: '80px',
              backgroundColor: vars.color.surfaceSecondary,
            }}
            color={
              campaignForEvent?.campaignState !== AdCampaignState.Active
                ? ColorsV2.error020
                : vars.color.textBrandV2
            }
            value={
              ((campaignForEvent.activeFlight?.flightSpend ?? 0) /
                (campaignForEvent.activeFlight?.flightBudget ?? 1)) *
              100
            }
          />
          <span
            style={{
              color:
                campaignForEvent?.campaignState !== AdCampaignState.Active
                  ? vars.color.textErrorV2
                  : vars.color.textBrandV2,
            }}
          >
            {formatCurrency(
              campaignForEvent.activeFlight?.flightBudget ?? 0,
              campaignForEvent.currencyCode ?? 'USD'
            )}
          </span>
        </Stack>
      )}
      {(showIcon || campaignForEvent) && (
        <BoostIconDisplay
          size={vars.iconSize.m}
          inactive={campaignForEvent?.campaignState !== AdCampaignState.Active}
        />
      )}
      {!campaignForEvent && (
        <span style={{ whiteSpace: 'nowrap' }}>
          <Content id={ContentId.BoostEvent} />
        </span>
      )}
    </Button>
  );
};
